import React, { useEffect, useState } from "react";
import { useOrganization } from "../../../context/OrganizationContext";
import { usePermissions } from "../../../context/PermissionsContext";
import {
  AlertBox,
  PrimaryButton,
  SecondaryButton,
  SkeletonBoard,
} from "../../UI/DesignStandards";
import {
  confirmOrganizationPlanCheckout,
  getOrganizationPlan,
  openOrganizationBillingPortal,
  startOrganizationPlanCheckout,
} from "../../../utility/organizationPlan";
import {
  formatPlanPrice,
  planCapDisplayLimit,
  planStatusCopy,
} from "../../../utility/planStatus";
import { PLAN_TIERS } from "../../../utility/planTiers";
import { tournamentCapUsageCopy } from "../../../utility/tournamentCaps";
import PlanFeatureList from "../../plans/PlanFeatureList";
import PlanTerms from "../../plans/PlanTerms";

const tierLines = (key) => {
  const tier = PLAN_TIERS.find((item) => item.key === key);
  return [...(tier?.notes || []), ...(tier?.points || [])];
};

const PRIORITY_POINTS = tierLines("priority");
const AI_ASSIST_LINES = tierLines("ai_assist");

const priceLabel = (price, fallback) => formatPlanPrice(price) || fallback;

const Plan = () => {
  const { currentOrganization } = useOrganization();
  const { canManageOrganization } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);
  const organizationId = currentOrganization?.id;

  useEffect(() => {
    if (!organizationId) return undefined;
    if (!canManageOrganization) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setError("");
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");
      try {
        const next =
          params.get("checkout") === "return" && sessionId
            ? await confirmOrganizationPlanCheckout(organizationId, sessionId)
            : await getOrganizationPlan(organizationId);
        if (!cancelled) setPlan(next);
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || "Could not load the plan.");
        }
        try {
          const next = await getOrganizationPlan(organizationId);
          if (!cancelled) setPlan(next);
        } catch (statusError) {
          if (!cancelled && !loadError?.message) {
            setError(statusError.message || "Could not load the plan.");
          }
        }
      } finally {
        if (params.get("checkout") === "return") {
          const url = new URL(window.location.href);
          url.searchParams.delete("checkout");
          url.searchParams.delete("session_id");
          window.history.replaceState(
            {},
            "",
            `${url.pathname}${url.search}${url.hash}`
          );
        }
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId, canManageOrganization]);

  const subscribe = async (planKey) => {
    if (!organizationId) return;
    setBusy(planKey);
    setError("");
    try {
      const result = await startOrganizationPlanCheckout(organizationId, planKey);
      if (result?.url) {
        window.location.assign(result.url);
        return;
      }
      setError("Could not open checkout.");
    } catch (checkoutError) {
      setError(checkoutError.message || "Could not open checkout.");
    } finally {
      setBusy("");
    }
  };

  const manageBilling = async () => {
    if (!organizationId) return;
    setBusy("portal");
    setError("");
    try {
      const result = await openOrganizationBillingPortal(organizationId);
      if (result?.url) {
        window.location.assign(result.url);
        return;
      }
      setError("Could not open billing.");
    } catch (portalError) {
      setError(portalError.message || "Could not open billing.");
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return (
      <div
        className="w-full max-w-2xl mx-auto space-y-6"
        role="status"
        aria-label="Loading plan"
      >
        <SkeletonBoard rows={4} />
      </div>
    );
  }

  if (!canManageOrganization) {
    return (
      <div className="w-full max-w-2xl mx-auto">
        <div className="text-center py-12">
          <h2 className="heading-display text-2xl mb-2">Access denied</h2>
          <p className="text-brand-muted">
            You need permission to manage this organization.
          </p>
        </div>
      </div>
    );
  }

  const copy = planStatusCopy(plan || {});
  const displayLimit = planCapDisplayLimit(plan || {});
  const capCopy = tournamentCapUsageCopy({
    tournamentCount: plan?.tournamentCount,
    tournamentLimit: displayLimit,
  });
  const subscribed = plan?.state === "subscribed";
  const onAiAssist = subscribed && plan?.plan === "ai_assist";
  const canSubscribe =
    plan?.configured && !subscribed && plan?.subscriptionStatus !== "past_due";
  const canUpgradeToPriority =
    plan?.configured && onAiAssist && plan?.hasCustomer;
  const aiPrice = priceLabel(plan?.prices?.ai_assist, "$99/year");
  const priorityPrice = priceLabel(plan?.prices?.priority, "$149/year");

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6">
      <header className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
            Plan
          </h1>
        </div>
      </header>

      {error ? (
        <AlertBox type="error" title="There was a problem" message={error} />
      ) : null}
      {copy.paymentNote ? (
        <AlertBox type="error" title="Payment failed" message={copy.paymentNote} />
      ) : null}
      {copy.disputeNote ? (
        <AlertBox type="error" title="Dispute" message={copy.disputeNote} />
      ) : null}
      {capCopy.atCap ? (
        <AlertBox
          type="error"
          title="Tournament limit"
          message={capCopy.message}
        />
      ) : capCopy.nearCap ? (
        <AlertBox
          type="warning"
          title="Tournament limit"
          message={capCopy.message}
        />
      ) : null}

      <div className="card-board p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-md border border-brand-charcoal/20 bg-brand-cream px-2.5 py-1 text-sm font-display font-bold text-brand-charcoal">
            {copy.label}
          </span>
          {plan?.cancelAtPeriodEnd && subscribed ? (
            <span className="inline-flex items-center rounded-md border border-brand-charcoal/20 px-2.5 py-1 text-xs font-medium text-brand-muted">
              Cancel pending
            </span>
          ) : null}
        </div>
        <p className="text-sm text-brand-charcoal leading-relaxed">
          {copy.detail}
        </p>
        {plan?.tournamentCount != null && displayLimit != null ? (
          <p className="text-sm text-brand-muted">{capCopy.usage}</p>
        ) : null}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {plan?.hasCustomer ? (
            <PrimaryButton
              onClick={manageBilling}
              disabled={Boolean(busy)}
              loading={busy === "portal"}
            >
              Manage billing
            </PrimaryButton>
          ) : null}
          {plan?.invoiceUrl ? (
            <a
              href={plan.invoiceUrl}
              className="text-sm underline text-brand-gold-ink"
              target="_blank"
              rel="noopener noreferrer"
            >
              View invoice
            </a>
          ) : null}
        </div>
      </div>

      {canSubscribe ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="card-board p-5 space-y-3 ring-2 ring-brand-gold">
            <h2 className="font-display font-bold text-lg text-brand-charcoal">
              AI Assist
            </h2>
            <p className="text-sm font-display font-bold text-brand-charcoal">
              {aiPrice}
            </p>
            <PlanFeatureList
              lines={AI_ASSIST_LINES}
              className="space-y-1.5 text-sm text-brand-charcoal list-disc pl-5"
            />
            <PrimaryButton
              onClick={() => subscribe("ai_assist")}
              loading={busy === "ai_assist"}
              disabled={Boolean(busy)}
            >
              Subscribe to AI Assist
            </PrimaryButton>
          </div>
          <div className="card-board p-5 space-y-3">
            <h2 className="font-display font-bold text-lg text-brand-charcoal">
              Priority
            </h2>
            <p className="text-sm font-display font-bold text-brand-charcoal">
              {priorityPrice}
            </p>
            <PlanFeatureList
              lines={PRIORITY_POINTS}
              className="space-y-1.5 text-sm text-brand-charcoal list-disc pl-5"
            />
            <SecondaryButton
              onClick={() => subscribe("priority")}
              loading={busy === "priority"}
              disabled={Boolean(busy)}
            >
              Subscribe to Priority
            </SecondaryButton>
          </div>
        </div>
      ) : null}

      {onAiAssist && canUpgradeToPriority ? (
        <div className="card-board p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-1">
            <h2 className="font-display font-bold text-lg text-brand-charcoal">
              Switch to Priority
            </h2>
            <p className="text-sm text-brand-muted">
              $149/year for unlimited tournaments. Includes AI Assist. You
              switch in Manage billing.
            </p>
          </div>
          <PrimaryButton
            onClick={manageBilling}
            loading={busy === "portal"}
            disabled={Boolean(busy)}
          >
            Switch to Priority
          </PrimaryButton>
        </div>
      ) : null}

      <PlanTerms variant="compact" />

      {plan && plan.configured === false ? (
        <p className="text-sm text-brand-muted">
          Plan billing is not configured on this site.
        </p>
      ) : null}
      {plan?.taxEnabled ? (
        <p className="text-sm text-brand-muted">Tax is added at checkout.</p>
      ) : null}
    </div>
  );
};

export default Plan;
